import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Table, Form, Dropdown, EmptyValue } from '@/components/ui';
import { Effort, FuelSurchargeRateBasis } from '../types/Effort';
import { formatDateForDisplay } from '../utils/dateUtils';
import ServerPagination from './ServerPagination';
import { effortService } from '../services/effortService';
import {
  calculateLabourLineTotal,
  effortNumericLineCost,
  formatLabourOtBreakdownText,
} from '../utils/labourOvertimeDisplay';
import { formatDollars } from '../utils/formatDollars';
import { usePermissions } from '../hooks/usePermissions';

interface TaskEffortsTableProps {
  efforts: Effort[];
  className?: string;
  columns?: {
    type?: boolean;
    date?: boolean;
    employeeName?: boolean;
    description?: boolean;
    accomplishment?: boolean;
    quantity?: boolean;
    unitOfMeasure?: boolean;
    rate?: boolean;
    /** Vehicle rows: applied fuel $/hr and line fuel total when surcharge is on. */
    fuelSurcharge?: boolean;
    totalCost?: boolean;
    costWithMarkup?: boolean;
    comments?: boolean;
    poNumber?: boolean;
    /** Labour rows: persisted straight / OT hour split (DRM). */
    otBreakdown?: boolean;
    timecard?: boolean;
  };
  projectMarkups?: {
    vehicleMarkup?: number;
    materialMarkup?: number;
    labourMarkup?: number;
    subcontractorMarkup?: number;
  };
  /** When true and user has permission, vehicle rows with a timecard can toggle fuel surcharge. */
  canToggleVehicleFuel?: boolean;
  /** Called with the server effort after a successful fuel PATCH (merge into parent task state). */
  onVehicleFuelEffortPatched?: (effort: Effort) => void;
  /** Called after a successful PATCH only when `onVehicleFuelEffortPatched` is not set (parent has no merge handler). */
  onVehicleFuelRefresh?: () => void | Promise<void>;
}

// W-L4: the string formatters below return '-' / an en dash for "no value"; in a cell that is
// rendered as the shared empty marker (muted, read as "None" by screen readers).
const asCell = (value: React.ReactNode): React.ReactNode =>
  value === '-' || value === '–' || value === '' || value == null ? <EmptyValue /> : value;

// Utility function to format text in sentence case
const formatSentenceCase = (text: string | null | undefined, type?: string): string => {
  if (!text) return '-';
  
  // Special handling for Labour descriptions - convert to uppercase
  if (type === 'Labour') {
    return text.toUpperCase();
  }
  
  // Handle underscore case first (e.g., "some_text" -> "Some Text")
  const withoutUnderscores = text.replace(/_/g, ' ');
  // Split into words, capitalize first letter of each word
  return withoutUnderscores
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

const defaultColumns = {
  type: true,
  date: true,
  employeeName: true,
  description: true,
  accomplishment: true,
  quantity: true,
  unitOfMeasure: true,
  rate: true,
  fuelSurcharge: false,
  totalCost: true,
  costWithMarkup: false,
  comments: true,
  poNumber: false, // Only show PO column on Project screen
  otBreakdown: true,
  timecard: true
};

const isVehicleEffort = (effort: Effort) => (effort.type || '').toLowerCase() === 'vehicle';

const hasAppliedFuelSurcharge = (effort: Effort) =>
  Boolean(effort.fuelSurchargeApplied) && (effort.fuelSurchargePerHour ?? 0) > 0;

const hasTimecardForm = (effort: Effort) => {
  const formId = effort.formId;
  return formId != null && String(formId) !== '0' && Number(formId) !== 0;
};

const formatFuelRateBasisLabel = (basis: FuelSurchargeRateBasis | null | undefined): string | null => {
  if (basis === 'ON_ROAD') return 'On-road rate';
  if (basis === 'OFF_ROAD') return 'Off-road rate';
  return null;
};

const CUSTOM_FUEL_OPTIONS: { basis: FuelSurchargeRateBasis; label: string }[] = [
  { basis: 'ON_ROAD', label: 'On-road' },
  { basis: 'OFF_ROAD', label: 'Off-road' },
];

const TaskEffortsTable: React.FC<TaskEffortsTableProps> = ({ 
  efforts, 
  className = '',
  columns: providedColumns,
  projectMarkups,
  canToggleVehicleFuel = false,
  onVehicleFuelEffortPatched,
  onVehicleFuelRefresh
}) => {
  const { hasPermission } = usePermissions();
  const canCustomFuelOverride = hasPermission('fuel:custom_surcharge_override');
  const [sortField, setSortField] = useState<keyof Effort>('type');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [groupBy, setGroupBy] = useState<keyof Effort | null>('type');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [togglingFuelEffortId, setTogglingFuelEffortId] = useState<number | null>(null);
  /** Effort id when user opened the custom fuel surcharge sub-menu. */
  const [customFuelMenuEffortId, setCustomFuelMenuEffortId] = useState<number | null>(null);


  // Reset to first page when sorting or grouping changes
  React.useEffect(() => {
    setCurrentPage(1);
  }, [sortField, sortDirection, groupBy]);

  if (!efforts || efforts.length === 0) {
    return (
      <div className="tw-text-center tw-text-content-muted tw-p-3">
        No efforts recorded for this task
      </div>
    );
  }

  const handleSort = (field: keyof Effort) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const getSortIcon = (field: keyof Effort) => {
    if (sortField !== field) return <i className="bi bi-arrow-down-up tw-text-content-muted tw-ml-1"></i>;
    return sortDirection === 'asc'
      ? <i className="bi bi-sort-down-alt tw-ml-1"></i>
      : <i className="bi bi-sort-down tw-ml-1"></i>;
  };

  const calculateTotalCost = (effort: Effort) => {
    if (effort.quantity === null || effort.quantity === undefined) {
      return '-';
    }
    // For Other efforts: display raw cost (quantity only); markups apply only to revenue
    const isOther = effort.type === 'Other' || effort.type === 'other';
    const isLabour = (effort.type || '').toLowerCase() === 'labour';
    const cost = isOther
      ? effort.quantity
      : isLabour
        ? calculateLabourLineTotal(effort)
        : (effort.rate ?? 0) * effort.quantity;
    if (!isOther && !isLabour && (effort.rate === null || effort.rate === undefined)) {
      return '-';
    }
    if (isLabour && (effort.rate === null || effort.rate === undefined)) {
      return '-';
    }
    return formatDollars(cost);
  };

  const calculateCostWithMarkup = (effort: Effort) => {
    if (effort.quantity === null || effort.quantity === undefined || 
        effort.rate === null || effort.rate === undefined || !projectMarkups) {
      return calculateTotalCost(effort);
    }

    const isLabour = (effort.type || '').toLowerCase() === 'labour';
    const baseCost = isLabour
      ? calculateLabourLineTotal(effort)
      : effort.quantity * effort.rate;
    let markup = 0;

    // Determine markup based on effort type
    switch (effort.type?.toLowerCase()) {
      case 'vehicle':
        markup = (projectMarkups.vehicleMarkup || 0) / 100;
        break;
      case 'material':
        markup = (projectMarkups.materialMarkup || 0) / 100;
        break;
      case 'labour':
        markup = (projectMarkups.labourMarkup || 0) / 100;
        break;
      case 'subcontractor':
        markup = (projectMarkups.subcontractorMarkup || 0) / 100;
        break;
      case 'other':
        // For Other costs, the rate already includes markup (quantity = cost, rate = markup multiplier)
        // So no additional markup calculation needed
        markup = 0;
        break;
      default:
        markup = 0; // No markup for other types
    }

    const costWithMarkup = baseCost * (1 + markup);
    return formatDollars(costWithMarkup);
  };

  const formatCurrency = (value: number | null | undefined) => {
    if (value === null || value === undefined) return '-';
    return formatDollars(value);
  };

  const displayRateForEffort = (effort: Effort): number | null | undefined => {
    if (isVehicleEffort(effort) || hasAppliedFuelSurcharge(effort)) {
      const base = effort.baseRate ?? effort.rate;
      return base;
    }
    return effort.rate;
  };

  const formatRateOrMarkup = (effort: Effort) => {
    const displayRate = displayRateForEffort(effort);
    if (displayRate === null || displayRate === undefined) return '-';
    
    // For "Other" type efforts, display as percentage markup
    if (effort.type?.toLowerCase() === 'other') {
      const markupPercentage = (displayRate - 1) * 100;
      return `${markupPercentage.toFixed(0)}%`;
    }
    
    // For all other types, display as currency (rate)
    return formatCurrency(displayRate);
  };

  const formatFuelSurchargeCell = (effort: Effort) => {
    if (!isVehicleEffort(effort) && !hasAppliedFuelSurcharge(effort)) return '–';
    const applied = Boolean(effort.fuelSurchargeApplied);
    const perHr = effort.fuelSurchargePerHour;
    if (!applied || perHr == null || perHr <= 0) return '–';
    const qty = effort.quantity ?? 0;
    const lineFuel = qty * perHr;
    const basisLabel = formatFuelRateBasisLabel(effort.fuelSurchargeRateBasis);
    return (
      <span>
        {formatCurrency(perHr)}/hr
        {basisLabel && (
          <span className="tw-text-content-muted tw-text-sm tw-block">{basisLabel}</span>
        )}
        {qty > 0 && (
          <span className="tw-text-content-muted tw-text-sm tw-block">
            Line: {formatCurrency(lineFuel)}
          </span>
        )}
      </span>
    );
  };

  const handleVehicleFuelToggle = async (
    effort: Effort,
    apply: boolean,
    rateBasis?: FuelSurchargeRateBasis
  ) => {
    if (!effort.id) return;
    setTogglingFuelEffortId(effort.id);
    try {
      const payload =
        apply && rateBasis
          ? { fuelSurchargeApplied: true, fuelSurchargeRateBasis: rateBasis }
          : { fuelSurchargeApplied: apply };
      const updated = await effortService.patchVehicleFuelSurcharge(effort.id, payload);
      setCustomFuelMenuEffortId(null);
      onVehicleFuelEffortPatched?.(updated);
      if (!onVehicleFuelEffortPatched) {
        await onVehicleFuelRefresh?.();
      }
    } catch (err) {
      console.error('TaskEffortsTable: fuel surcharge toggle failed', err);
    } finally {
      setTogglingFuelEffortId(null);
    }
  };

  const sortedEfforts = [...efforts].sort((a, b) => {
    if (!a[sortField] && !b[sortField]) return 0;
    if (!a[sortField]) return 1;
    if (!b[sortField]) return -1;

    let comparison = 0;
    if (sortField === 'date') {
      const dateA = a.date ? new Date(a.date).getTime() : 0;
      const dateB = b.date ? new Date(b.date).getTime() : 0;
      comparison = dateA - dateB;
    } else if (typeof a[sortField] === 'string') {
      comparison = (a[sortField] as string).localeCompare(b[sortField] as string);
    } else if (typeof a[sortField] === 'number') {
      comparison = (a[sortField] as number) - (b[sortField] as number);
    }

    return sortDirection === 'asc' ? comparison : -comparison;
  });

  // Calculate pagination
  const totalPages = Math.ceil(sortedEfforts.length / pageSize);
  const paginatedEfforts = sortedEfforts.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  );

  // Update groupedEfforts to handle date grouping
  const groupedEfforts = groupBy
    ? sortedEfforts.reduce((groups, effort) => {
        let key;
        if (groupBy === 'date') {
          key = effort.date 
            ? formatDateForDisplay(effort.date)
            : 'Unspecified';
        } else if (groupBy === 'type') {
          key = formatSentenceCase(effort[groupBy]?.toString() || 'Unspecified');
        } else {
          key = effort[groupBy]?.toString() || 'Unspecified';
        }
        return {
          ...groups,
          [key]: [...(groups[key] || []), effort],
        };
      }, {} as Record<string, Effort[]>)
    : { all: paginatedEfforts };

  const renderPagination = () => {
    return (
      <ServerPagination
        currentPage={currentPage}
        totalPages={totalPages}
        totalCount={sortedEfforts.length}
        pageSize={pageSize}
        onPageChange={setCurrentPage}
        onPageSizeChange={(size) => {
          setPageSize(size);
          setCurrentPage(1);
        }}
        maxVisiblePages={5}
        className="tw-mt-3"
      />
    );
  };

  // Set up columns with costWithMarkup only when markups are provided
  const columns = {
    ...defaultColumns,
    costWithMarkup: !!projectMarkups,
    ...providedColumns
  };

  /** Span for "Group Total" / "Total" label: all columns from Type through Accomplishment (before OT, qty, unit, rate, …). */
  const effortTableLabelColSpan =
    Object.values(columns).filter(Boolean).length -
    (columns.otBreakdown ? 1 : 0) -
    (columns.quantity ? 1 : 0) -
    (columns.unitOfMeasure ? 1 : 0) -
    (columns.rate ? 1 : 0) -
    (columns.fuelSurcharge ? 1 : 0) -
    (columns.totalCost ? 1 : 0) -
    (columns.costWithMarkup ? 1 : 0) -
    (columns.timecard ? 1 : 0);

  // Row actions (view timecard / fuel-surcharge toggle) — shared by the desktop table
  // cell and the mobile card so the dropdown logic isn't duplicated. Returns null when
  // there's nothing actionable.
  const renderEffortActions = (effort: Effort, index: number): React.ReactNode => {
    const timecard = hasTimecardForm(effort);
    const vehicle = isVehicleEffort(effort);
    const showFuelToggle = canToggleVehicleFuel && vehicle && timecard;
    const showMenu = timecard || showFuelToggle;
    if (!showMenu) return null;

    const busy = togglingFuelEffortId === effort.id;
    const fuelOn = Boolean(effort.fuelSurchargeApplied);
    // Shared Headless UI menu (L8: no Bootstrap JS). Choosing an action closes it; the two items
    // that switch to and from the custom fuel options cancel the click, so the menu stays open.
    return (
      <Dropdown align="end" data-effort-actions-dropdown data-effort-id={effort.id}>
        <Dropdown.Toggle
          caret={false}
          className="!tw-border-0 !tw-bg-transparent !tw-p-0 tw-text-black hover:tw-underline disabled:tw-opacity-65"
          id={`effort-actions-${effort.id}-${index}`}
          aria-label="Actions"
          title="Actions"
          style={{ minWidth: '24px', minHeight: '24px' }}
          disabled={busy}
          onClick={() => setCustomFuelMenuEffortId(null)}
        >
          <i className="bi bi-three-dots-vertical tw-text-xl" />
        </Dropdown.Toggle>
        <Dropdown.Menu aria-labelledby={`effort-actions-${effort.id}-${index}`}>
          {timecard && (
            <Dropdown.Item as={Link} {...({ to: `/submissions?formId=${effort.formId}` } as object)}>
              <i className="bi bi-card-text tw-mr-2" aria-hidden />
              View timecard
            </Dropdown.Item>
          )}
          {showFuelToggle && fuelOn && (
            <Dropdown.Item type="button" disabled={busy} onClick={() => handleVehicleFuelToggle(effort, false)}>
              <i className="bi bi-fuel-pump tw-mr-2" aria-hidden />
              Remove fuel surcharge
            </Dropdown.Item>
          )}
          {showFuelToggle && !fuelOn && canCustomFuelOverride && (
            customFuelMenuEffortId === effort.id ? (
              <>
                {CUSTOM_FUEL_OPTIONS.map(({ basis, label }) => (
                  <Dropdown.Item
                    key={basis}
                    type="button"
                    disabled={busy}
                    onClick={() => handleVehicleFuelToggle(effort, true, basis)}
                  >
                    <i className="bi bi-fuel-pump tw-mr-2" aria-hidden />
                    {label}
                  </Dropdown.Item>
                ))}
                <Dropdown.Divider />
                <Dropdown.Item
                  type="button"
                  disabled={busy}
                  onClick={(e) => { e.preventDefault(); setCustomFuelMenuEffortId(null); }}
                >
                  <i className="bi bi-arrow-left tw-mr-2" aria-hidden />
                  Back
                </Dropdown.Item>
              </>
            ) : (
              <>
                <Dropdown.Item
                  type="button"
                  disabled={busy}
                  onClick={() => handleVehicleFuelToggle(effort, true, 'DEFAULT')}
                >
                  <i className="bi bi-fuel-pump tw-mr-2" aria-hidden />
                  Apply fuel surcharge (Default)
                </Dropdown.Item>
                <Dropdown.Item
                  type="button"
                  disabled={busy}
                  onClick={(e) => { e.preventDefault(); if (effort.id) setCustomFuelMenuEffortId(effort.id); }}
                >
                  <i className="bi bi-fuel-pump tw-mr-2" aria-hidden />
                  Custom fuel surcharge
                  <i className="bi bi-chevron-right tw-float-right tw-mt-1" aria-hidden />
                </Dropdown.Item>
              </>
            )
          )}
          {showFuelToggle && !fuelOn && !canCustomFuelOverride && (
            <Dropdown.Item type="button" disabled={busy} onClick={() => handleVehicleFuelToggle(effort, true)}>
              <i className="bi bi-fuel-pump tw-mr-2" aria-hidden />
              Apply fuel surcharge
            </Dropdown.Item>
          )}
        </Dropdown.Menu>
      </Dropdown>
    );
  };

  return (
    <div>
      <div className="tw-mb-3">
        <label className="tw-mr-2">Group by:</label>
        <Form.Select
          size="sm"
          className="tw-inline-block tw-w-auto"
          value={groupBy || ''}
          onChange={(e) => setGroupBy(e.target.value as keyof Effort || null)}
        >
          <option value="">None</option>
          <option value="type">Type</option>
          <option value="employeeName">Employee</option>
          <option value="date">Date</option>
        </Form.Select>
      </div>

      <div className="tw-hidden md:tw-block">
      <Table size="sm" bordered className={className}>
        <thead className="tw-bg-surface">
          <tr>
            {columns.type && (
              <th onClick={() => handleSort('type')} style={{ cursor: 'pointer', width: '10%' }}>
                Type {getSortIcon('type')}
              </th>
            )}
            {columns.date && (
              <th onClick={() => handleSort('date')} style={{ cursor: 'pointer', width: '10%' }}>
                Date {getSortIcon('date')}
              </th>
            )}
            {columns.employeeName && (
              <th onClick={() => handleSort('employeeName')} style={{ cursor: 'pointer', width: '12%' }}>
                Employee {getSortIcon('employeeName')}
              </th>
            )}
            {columns.description && (
              <th onClick={() => handleSort('description')} style={{ cursor: 'pointer', width: '15%' }}>
                Description {getSortIcon('description')}
              </th>
            )}
            {columns.poNumber && (
              <th onClick={() => handleSort('poNumber')} style={{ cursor: 'pointer', width: '10%' }}>
                PO Number {getSortIcon('poNumber')}
              </th>
            )}
            {columns.comments && (
              <th onClick={() => handleSort('comments')} style={{ cursor: 'pointer', width: '10%' }}>
                Comments {getSortIcon('comments')}
              </th>
            )}
            {columns.accomplishment && (
              <th onClick={() => handleSort('accomplishment')} style={{ cursor: 'pointer', width: '10%' }}>
                Accomplishment {getSortIcon('accomplishment')}
              </th>
            )}
            {columns.otBreakdown && (
              <th style={{ width: '12%' }}>OT Breakdown</th>
            )}
            {columns.quantity && (
              <th onClick={() => handleSort('quantity')} style={{ cursor: 'pointer', width: '8%' }}>
                Quantity {getSortIcon('quantity')}
              </th>
            )}
            {columns.unitOfMeasure && (
              <th onClick={() => handleSort('unitOfMeasure')} style={{ cursor: 'pointer', width: '7%' }}>
                Unit {getSortIcon('unitOfMeasure')}
              </th>
            )}
            {columns.rate && (
              <th onClick={() => handleSort('rate')} style={{ cursor: 'pointer', width: '8%' }}>
                {columns.fuelSurcharge ? 'Base Rate' : 'Rate/Markup'} {getSortIcon('rate')}
              </th>
            )}
            {columns.fuelSurcharge && (
              <th style={{ width: '10%' }}>Fuel Surcharge</th>
            )}
            {columns.totalCost && (
              <th style={{ width: '10%' }}>Total Cost</th>
            )}
            {columns.costWithMarkup && (
              <th style={{ width: '10%' }}>Cost with Markup</th>
            )}
            {columns.timecard && (
              <th style={{ width: '8%' }}>Actions</th>
            )}
          </tr>
        </thead>
        <tbody>
          {Object.entries(groupedEfforts).map(([group, groupEfforts]) => (
            <React.Fragment key={group}>
              {groupBy && (
                <tr className="tw-bg-surface-hover">
                  <td colSpan={Object.values(columns).filter(Boolean).length}>
                    <strong>{group}</strong>
                  </td>
                </tr>
              )}
              {groupEfforts.map((effort, index) => (
                <tr key={effort.id || index}>
                  {columns.type && <td>{asCell(formatSentenceCase(effort.type))}</td>}
                  {columns.date && <td className="tw-whitespace-nowrap">{effort.date ? formatDateForDisplay(effort.date) : <EmptyValue />}</td>}
                  {columns.employeeName && <td>{asCell(formatSentenceCase(effort.employeeName))}</td>}
                  {columns.description && <td>{asCell(formatSentenceCase(effort.description, effort.type))}</td>}
                  {columns.poNumber && <td>{effort.poNumber || <EmptyValue />}</td>}
                  {columns.comments && <td>{effort.comments || <EmptyValue />}</td>}
                  {columns.accomplishment && <td>{effort.accomplishment ? effort.accomplishment.toFixed(2) : <EmptyValue />}</td>}
                  {columns.otBreakdown && (
                    <td className="tw-text-sm tw-text-content-muted" style={{ whiteSpace: 'pre-line' }}>
                      {asCell(formatLabourOtBreakdownText(effort))}
                    </td>
                  )}
                  {columns.quantity && <td>{effort.quantity ?? <EmptyValue />}</td>}
                  {columns.unitOfMeasure && <td>{effort.unitOfMeasure || <EmptyValue />}</td>}
                  {columns.rate && <td>{asCell(formatRateOrMarkup(effort))}</td>}
                  {columns.fuelSurcharge && <td>{asCell(formatFuelSurchargeCell(effort))}</td>}
                  {columns.totalCost && <td>{asCell(calculateTotalCost(effort))}</td>}
                  {columns.costWithMarkup && <td>{asCell(calculateCostWithMarkup(effort))}</td>}
                  {columns.timecard && (
                    <td>{renderEffortActions(effort, index) ?? <EmptyValue />}</td>
                  )}
                </tr>
              ))}
              {groupBy && (
                <tr className="tw-bg-surface-alt">
                  <td colSpan={effortTableLabelColSpan}><strong>Group Total</strong></td>
                  {columns.otBreakdown && <td><EmptyValue /></td>}
                  {columns.quantity && (
                    <td>
                      {groupEfforts.reduce((sum, e) => sum + (e.quantity || 0), 0).toFixed(2)}
                    </td>
                  )}
                  {columns.unitOfMeasure && <td><EmptyValue /></td>}
                  {columns.rate && <td><EmptyValue /></td>}
                  {columns.fuelSurcharge && <td><EmptyValue /></td>}
                  {columns.totalCost && (
                    <td>
                      {formatCurrency(groupEfforts.reduce((sum, e) => sum + effortNumericLineCost(e), 0))}
                    </td>
                  )}
                  {columns.costWithMarkup && (
                    <td>
                      {formatCurrency(
                        groupEfforts.reduce((sum, e) => {
                          if (!projectMarkups) return sum;
                          const baseCost = effortNumericLineCost(e);
                          let markup = 0;
                          switch (e.type?.toLowerCase()) {
                            case 'vehicle':
                              markup = (projectMarkups.vehicleMarkup || 0) / 100;
                              break;
                            case 'material':
                              markup = (projectMarkups.materialMarkup || 0) / 100;
                              break;
                            case 'labour':
                              markup = (projectMarkups.labourMarkup || 0) / 100;
                              break;
                            case 'subcontractor':
                              markup = (projectMarkups.subcontractorMarkup || 0) / 100;
                              break;
                            case 'other':
                              markup = 0;
                              break;
                            default:
                              markup = 0;
                          }
                          return sum + baseCost * (1 + markup);
                        }, 0)
                      )}
                    </td>
                  )}
                  {columns.timecard && <td><EmptyValue /></td>}
                </tr>
              )}
            </React.Fragment>
          ))}
        </tbody>
        {!groupBy && (
          <tfoot className="tw-bg-surface-alt">
            <tr>
              <td colSpan={effortTableLabelColSpan}><strong>Total</strong></td>
              {columns.otBreakdown && <td><EmptyValue /></td>}
              {columns.quantity && (
                <td>
                  {sortedEfforts.reduce((sum, e) => sum + (e.quantity || 0), 0).toFixed(2)}
                </td>
              )}
              {columns.unitOfMeasure && <td><EmptyValue /></td>}
              {columns.rate && <td><EmptyValue /></td>}
              {columns.fuelSurcharge && <td><EmptyValue /></td>}
              {columns.totalCost && (
                <td>
                  {formatCurrency(sortedEfforts.reduce((sum, e) => sum + effortNumericLineCost(e), 0))}
                </td>
              )}
              {columns.costWithMarkup && (
                <td>
                  {formatCurrency(
                    sortedEfforts.reduce((sum, e) => {
                      if (!projectMarkups) return sum;
                      const baseCost = effortNumericLineCost(e);
                      let markup = 0;
                      switch (e.type?.toLowerCase()) {
                        case 'vehicle':
                          markup = (projectMarkups.vehicleMarkup || 0) / 100;
                          break;
                        case 'material':
                          markup = (projectMarkups.materialMarkup || 0) / 100;
                          break;
                        case 'labour':
                          markup = (projectMarkups.labourMarkup || 0) / 100;
                          break;
                        case 'subcontractor':
                          markup = (projectMarkups.subcontractorMarkup || 0) / 100;
                          break;
                        case 'other':
                          markup = 0;
                          break;
                        default:
                          markup = 0;
                      }
                      return sum + baseCost * (1 + markup);
                    }, 0)
                  )}
                </td>
              )}
              {columns.timecard && <td><EmptyValue /></td>}
            </tr>
          </tfoot>
        )}
      </Table>
      </div>

      {/* Mobile (< md): stacked effort cards — the wide efforts table can't fit a phone.
          Each effort is a card with Type + Total, description, and key fields; group
          headers/totals and the row actions (view timecard / fuel) are preserved. */}
      <div className="tw-flex tw-flex-col tw-gap-2 md:tw-hidden">
        {Object.entries(groupedEfforts).map(([group, groupEfforts]) => (
          <React.Fragment key={`m-${group}`}>
            {groupBy && (
              <div className="tw-mt-1 tw-text-xs tw-font-semibold tw-tracking-wide tw-text-content-secondary">
                {group}
              </div>
            )}
            {groupEfforts.map((effort, index) => (
              <div
                key={effort.id || `m-${group}-${index}`}
                className="tw-rounded-lg tw-border tw-border-line-input tw-bg-surface tw-p-3 tw-shadow-sm"
              >
                <div className="tw-flex tw-items-start tw-justify-between tw-gap-2">
                  <span className="tw-font-semibold tw-text-content">{formatSentenceCase(effort.type)}</span>
                  <div className="tw-flex tw-shrink-0 tw-items-center tw-gap-2">
                    {columns.totalCost && (
                      <span className="tw-font-semibold tw-tabular-nums tw-text-ink">{calculateTotalCost(effort)}</span>
                    )}
                    {columns.timecard && renderEffortActions(effort, index)}
                  </div>
                </div>
                {columns.description && effort.description && (
                  <div className="tw-mt-0.5 tw-break-words tw-text-sm tw-text-content-secondary">
                    {formatSentenceCase(effort.description, effort.type)}
                  </div>
                )}
                <div className="tw-mt-2 tw-grid tw-grid-cols-2 tw-gap-x-3 tw-gap-y-1.5 tw-text-sm">
                  {columns.date && effort.date && (
                    <div className="tw-min-w-0">
                      <div className="tw-text-xs tw-tracking-wide tw-text-content-muted">Date</div>
                      <div className="tw-text-content">{formatDateForDisplay(effort.date)}</div>
                    </div>
                  )}
                  {columns.employeeName && effort.employeeName && (
                    <div className="tw-min-w-0">
                      <div className="tw-text-xs tw-tracking-wide tw-text-content-muted">Employee</div>
                      <div className="tw-truncate tw-text-content">{formatSentenceCase(effort.employeeName)}</div>
                    </div>
                  )}
                  {columns.quantity && (
                    <div className="tw-min-w-0">
                      <div className="tw-text-xs tw-tracking-wide tw-text-content-muted">Quantity</div>
                      <div className="tw-tabular-nums tw-text-content">
                        {effort.quantity ?? <EmptyValue />} {columns.unitOfMeasure ? effort.unitOfMeasure || '' : ''}
                      </div>
                    </div>
                  )}
                  {columns.rate && (
                    <div className="tw-min-w-0">
                      <div className="tw-text-xs tw-tracking-wide tw-text-content-muted">
                        {columns.fuelSurcharge ? 'Base Rate' : 'Rate/Markup'}
                      </div>
                      <div className="tw-tabular-nums tw-text-content">{formatRateOrMarkup(effort)}</div>
                    </div>
                  )}
                  {columns.costWithMarkup && (
                    <div className="tw-min-w-0">
                      <div className="tw-text-xs tw-tracking-wide tw-text-content-muted">With markup</div>
                      <div className="tw-tabular-nums tw-text-content">{calculateCostWithMarkup(effort)}</div>
                    </div>
                  )}
                </div>
                {columns.otBreakdown &&
                  formatLabourOtBreakdownText(effort) &&
                  formatLabourOtBreakdownText(effort) !== '–' && (
                    <div className="tw-mt-1 tw-whitespace-pre-line tw-text-xs tw-text-content-muted">
                      {formatLabourOtBreakdownText(effort)}
                    </div>
                  )}
              </div>
            ))}
            {groupBy && (
              <div className="tw-flex tw-items-center tw-justify-between tw-rounded-lg tw-bg-surface-alt tw-px-3 tw-py-2 tw-text-sm tw-font-semibold tw-text-content">
                <span>Group Total</span>
                <span className="tw-tabular-nums">
                  {formatCurrency(groupEfforts.reduce((sum, e) => sum + effortNumericLineCost(e), 0))}
                </span>
              </div>
            )}
          </React.Fragment>
        ))}
        {!groupBy && (
          <div className="tw-flex tw-items-center tw-justify-between tw-rounded-lg tw-bg-surface-hover tw-px-3 tw-py-2.5 tw-font-bold tw-text-content">
            <span>Total</span>
            <span className="tw-tabular-nums">
              {formatCurrency(sortedEfforts.reduce((sum, e) => sum + effortNumericLineCost(e), 0))}
            </span>
          </div>
        )}
      </div>

      {!groupBy && renderPagination()}
    </div>
  );
};

export default TaskEffortsTable; 